<script setup>
/**
 * 👍/👎 numa resposta do Lumos (Fase 2, 08/10/2026).
 *
 * Um toque basta: a nota grava na hora. No 👎 abre um campo opcional,
 * "o que faltou?" — é dali que sai o que corrigir. O administrador lê tudo
 * no registro da Observabilidade (PerguntasAoLumos). A resposta do servidor
 * volta para o store, então a nota sobrevive a fechar e abrir o painel.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import CopilotMessagesAPI from 'dashboard/api/captain/copilotMessages';

const props = defineProps({
  threadId: { type: Number, required: true },
  mensagemId: { type: Number, required: true },
  avaliacao: { type: Object, default: null },
});

const { t } = useI18n();
const store = useStore();

const nota = ref(props.avaliacao?.nota || null);
const comentario = ref(props.avaliacao?.comentario || '');
const pedindoComentario = ref(false);
const enviado = ref(Boolean(props.avaliacao?.comentario));
const salvando = ref(false);
const falhou = ref(false);

const salvar = async (novaNota, texto) => {
  salvando.value = true;
  falhou.value = false;
  try {
    const { data } = await CopilotMessagesAPI.avaliar({
      threadId: props.threadId,
      id: props.mensagemId,
      nota: novaNota,
      comentario: texto,
    });
    await store.dispatch('copilotMessages/upsert', data);
    return true;
  } catch {
    falhou.value = true;
    return false;
  } finally {
    salvando.value = false;
  }
};

const escolher = async novaNota => {
  if (salvando.value) return;
  const anterior = nota.value;
  nota.value = novaNota;
  enviado.value = false;
  pedindoComentario.value = novaNota === 'ruim';
  if (!(await salvar(novaNota))) nota.value = anterior;
};

const enviarComentario = async () => {
  if (!comentario.value.trim()) return;
  if (await salvar('ruim', comentario.value)) {
    pedindoComentario.value = false;
    enviado.value = true;
  }
};

// Um agradecimento só: depois do comentário, quem fala é o "Anotado".
const agradece = computed(
  () => nota.value && !pedindoComentario.value && !enviado.value
);
</script>

<template>
  <div class="lumos-aval">
    <div class="lumos-aval__linha">
      <button
        v-tooltip="t('CAPTAIN.COPILOT.AVALIACAO.BOA')"
        type="button"
        class="lumos-aval__nota"
        :class="{ 'lumos-aval__nota--boa': nota === 'boa' }"
        :aria-pressed="nota === 'boa'"
        :aria-label="t('CAPTAIN.COPILOT.AVALIACAO.BOA')"
        :disabled="salvando"
        @click="escolher('boa')"
      >
        <i
          :class="nota === 'boa' ? 'i-ph-thumbs-up-fill' : 'i-ph-thumbs-up'"
          aria-hidden="true"
        />
      </button>
      <button
        v-tooltip="t('CAPTAIN.COPILOT.AVALIACAO.RUIM')"
        type="button"
        class="lumos-aval__nota"
        :class="{ 'lumos-aval__nota--ruim': nota === 'ruim' }"
        :aria-pressed="nota === 'ruim'"
        :aria-label="t('CAPTAIN.COPILOT.AVALIACAO.RUIM')"
        :disabled="salvando"
        @click="escolher('ruim')"
      >
        <i
          :class="
            nota === 'ruim' ? 'i-ph-thumbs-down-fill' : 'i-ph-thumbs-down'
          "
          aria-hidden="true"
        />
      </button>
      <span v-if="falhou" class="lumos-aval__aviso lumos-aval__aviso--erro">
        {{ t('CAPTAIN.COPILOT.AVALIACAO.FALHOU') }}
      </span>
      <span v-else-if="agradece" class="lumos-aval__aviso" aria-live="polite">
        {{ t('CAPTAIN.COPILOT.AVALIACAO.OBRIGADO') }}
      </span>
    </div>
    <form
      v-if="pedindoComentario"
      class="lumos-aval__form"
      @submit.prevent="enviarComentario"
    >
      <label class="sr-only" :for="`aval-${mensagemId}`">
        {{ t('CAPTAIN.COPILOT.AVALIACAO.O_QUE_FALTOU') }}
      </label>
      <input
        :id="`aval-${mensagemId}`"
        v-model="comentario"
        type="text"
        maxlength="500"
        class="advup-campo reset-base lumos-aval__campo"
        :placeholder="t('CAPTAIN.COPILOT.AVALIACAO.O_QUE_FALTOU')"
      />
      <button
        type="submit"
        class="advup-btn lumos-aval__enviar"
        :disabled="salvando || !comentario.trim()"
      >
        {{ t('CAPTAIN.COPILOT.AVALIACAO.ENVIAR') }}
      </button>
    </form>
    <span v-else-if="enviado" class="lumos-aval__aviso">
      {{ t('CAPTAIN.COPILOT.AVALIACAO.ANOTADO') }}
    </span>
  </div>
</template>

<style scoped>
.lumos-aval {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  padding: 0 var(--esp-1);
}

.lumos-aval__linha {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
}

/* Ícone em repouso no slate-10 (4,3:1) e alvo de 32px; a escolha pinta na
   cor do que diz: teal bom, rubi ruim. */
.lumos-aval__nota {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: var(--r-3);
  background: transparent;
  color: rgb(var(--slate-10));
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva-pulo);
}

.lumos-aval__nota > i {
  font-size: var(--ico-2);
}

.lumos-aval__nota:hover {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
}

.lumos-aval__nota:active {
  transform: scale(0.92);
}

.lumos-aval__nota:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 1px;
}

.lumos-aval__nota--boa,
.lumos-aval__nota--boa:hover {
  background: rgb(var(--teal-9) / 12%);
  color: rgb(var(--teal-11));
}

.lumos-aval__nota--ruim,
.lumos-aval__nota--ruim:hover {
  background: rgb(var(--ruby-9) / 12%);
  color: rgb(var(--ruby-11));
}

.lumos-aval__aviso {
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-11));
}

.lumos-aval__aviso--erro {
  color: rgb(var(--ruby-11));
}

.lumos-aval__form {
  display: flex;
  gap: var(--esp-2);
}

.lumos-aval__campo {
  flex: 1;
  min-width: 0;
}

.lumos-aval__enviar {
  flex-shrink: 0;
}
</style>
